/**
 * Fix para Race Condition en el carrito de compras
 * Este archivo corrige los problemas de race condition en StaticCart.js
 * 
 * INSTRUCCIONES PARA APLICAR EN SHOPIFY:
 * 1. Sube este archivo a assets/cart-race-condition-fix.js en tu tema de Shopify
 * 2. Agrega esta línea en layout/theme.liquid ANTES del cierre de </body>:
 *    <script src="{{ 'cart-race-condition-fix.js' | asset_url }}" defer></script>
 */

(function() {
  'use strict';

  // Esperar a que el DOM y el código del tema estén listos
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    // Si ya está cargado, esperar un poco para que StaticCart se inicialice
    setTimeout(init, 100);
  }

  function init() {
    // Buscar la sección del carrito
    const cartSection = document.querySelector('[data-section-type="static-cart"]');
    if (!cartSection) {
      console.warn('Cart section not found, retrying...');
      setTimeout(init, 500);
      return;
    }

    // Obtener el ID de la sección
    const sectionId = cartSection.getAttribute('data-section-id');
    if (!sectionId) return;

    // Buscar el script que contiene los datos de la sección
    const sectionDataScript = document.querySelector(`script[data-section-id="${sectionId}"]`);
    if (!sectionDataScript) return;

    try {
      const sectionData = JSON.parse(sectionDataScript.textContent);
      
      // Esperar a que window.Theme esté disponible
      if (!window.Theme || !window.Theme.routes) {
        setTimeout(init, 100);
        return;
      }

      // Aplicar el parche
      patchStaticCart(sectionId, sectionData);
    } catch (e) {
      console.error('Error parsing cart section data:', e);
    }
  }

  // Función para determinar si un producto es vendido por FT (no por Spools)
  function isProductSoldByFoot(cartItem) {
    // Buscar las opciones del producto en el DOM del carrito
    const productOptions = cartItem.querySelectorAll('.cart-item-product-options__option');
    
    // Keywords que indican que el producto tiene standard lengths (Spools)
    const lengthKeywords = ['length', 'lenght', 'coil', 'spool', 'ft', 'feet', 'foot'];
    
    // Si no hay opciones, asumimos que es vendido por FT
    if (productOptions.length === 0) {
      return true;
    }
    
    // Verificar si alguna opción contiene keywords de standard lengths
    for (const option of productOptions) {
      const optionKey = option.querySelector('.cart-item-product-options__option-key');
      const optionValue = option.querySelector('.cart-item-product-options__option-value');
      
      if (optionKey && optionValue) {
        const keyText = optionKey.textContent.toLowerCase().trim();
        const valueText = optionValue.textContent.toLowerCase().trim();
        
        // Si la opción o su valor contiene keywords de standard lengths, NO es vendido por FT
        for (const keyword of lengthKeywords) {
          if (keyText.includes(keyword) || valueText.includes(keyword)) {
            return false; // Es vendido por Spools, no por FT
          }
        }
      }
    }
    
    // Si no encontramos keywords de standard lengths, es vendido por FT
    return true;
  }

  // Función para validar cantidad mínima de 25ft para productos vendidos por FT
  function validateMinimumQuantity(cartItem, quantity) {
    // Solo validar si es un producto vendido por FT
    if (!isProductSoldByFoot(cartItem)) {
      return { valid: true, message: null };
    }
    
    // Validar mínimo de 25ft
    if (quantity > 0 && quantity < 25) {
      return {
        valid: false,
        message: 'Minimum order is 25 ft for products sold by foot'
      };
    }
    
    return { valid: true, message: null };
  }

  // Función para mostrar mensaje de error en el carrito
  function showCartError(cartItem, message) {
    // Remover mensajes de error anteriores
    const existingError = cartItem.querySelector('.cart-item-error');
    if (existingError) {
      existingError.remove();
    }
    
    // Crear nuevo mensaje de error
    const errorDiv = document.createElement('div');
    errorDiv.className = 'cart-item-error';
    errorDiv.textContent = message;
    errorDiv.style.cssText = `
      display: block;
      background: linear-gradient(90deg, #ff4d4d, #d9534f);
      color: #fff;
      font-size: 13px;
      margin-top: 8px;
      padding: 8px 10px;
      border-radius: 6px;
      font-weight: 500;
      opacity: 0;
      transform: translateY(-10px);
      transition: opacity 0.3s ease-out, transform 0.3s ease-out;
    `;
    
    // Insertar el mensaje después del input de cantidad
    const quantityContainer = cartItem.querySelector('.cart-item__quantity');
    if (quantityContainer) {
      quantityContainer.appendChild(errorDiv);
      
      // Animar entrada
      requestAnimationFrame(() => {
        errorDiv.style.opacity = '1';
        errorDiv.style.transform = 'translateY(0)';
      });
      
      // Remover el mensaje después de 5 segundos
      setTimeout(() => {
        if (errorDiv.parentNode) {
          errorDiv.style.opacity = '0';
          errorDiv.style.transform = 'translateY(-10px)';
          setTimeout(() => {
            if (errorDiv.parentNode) {
              errorDiv.remove();
            }
          }, 300);
        }
      }, 5000);
    }
  }

  function patchStaticCart(sectionId, sectionData) {
    // Esta función se ejecutará después de que StaticCart se haya inicializado
    // Necesitamos interceptar los métodos problemáticos
    
    // Buscar todos los quantity-selector custom elements
    const quantitySelectors = document.querySelectorAll('quantity-selector');
    
    if (quantitySelectors.length === 0) {
      // Si no hay selectors, esperar un poco más (puede que el carrito esté vacío o aún se esté cargando)
      setTimeout(() => patchStaticCart(sectionId, sectionData), 200);
      return;
    }

    // Crear un objeto para rastrear las peticiones en curso
    const cartUpdateState = {
      pendingRequest: null,
      abortController: null,
      lastUpdateId: 0,
      updateTimeout: null
    };

    // Interceptar eventos del custom element quantity-selector
    quantitySelectors.forEach(selector => {
      const cartItem = selector.closest('[data-cartitem]');
      if (!cartItem) return;

      const itemKey = cartItem.getAttribute('data-cartitem-key');
      if (!itemKey) return;

      const input = selector.querySelector('[data-quantity-input]');
      if (!input) return;

      let lastValue = parseInt(input.value, 10);
      if (isNaN(lastValue) || lastValue < 1) {
        lastValue = 1;
      }

      // Escuchar el evento cart:quantity que dispara el custom element
      selector.addEventListener('cart:quantity', function(e) {
        const [key, qty] = e.detail;
        
        // Validar que el key coincida
        if (key !== itemKey) return;

        // Validar cantidad
        let quantity = parseInt(qty, 10);
        if (isNaN(quantity) || quantity < 0) {
          console.warn('Invalid quantity:', qty);
          return;
        }

        // VALIDACIÓN: Verificar mínimo de 25ft para productos vendidos por FT
        const validation = validateMinimumQuantity(cartItem, quantity);
        if (!validation.valid) {
          // Si es menor a 25ft, establecer automáticamente a 25ft
          quantity = 25;
          
          // Actualizar el input a 25
          input.value = 25;
          lastValue = 25;
          
          // Mostrar mensaje de error informativo
          showCartError(cartItem, validation.message);
          
          // Continuar con la actualización del carrito con 25ft
          // (no retornar, dejar que continúe el flujo normal)
        } else {
          // Remover mensajes de error si la validación pasó
          const existingError = cartItem.querySelector('.cart-item-error');
          if (existingError) {
            existingError.remove();
          }
        }

        // Actualizar lastValue
        lastValue = quantity;

        // Cancelar petición anterior si existe
        if (cartUpdateState.abortController) {
          cartUpdateState.abortController.abort();
        }

        // Limpiar timeout anterior
        if (cartUpdateState.updateTimeout) {
          clearTimeout(cartUpdateState.updateTimeout);
        }

        // Crear nuevo AbortController
        cartUpdateState.abortController = new AbortController();
        const updateId = ++cartUpdateState.lastUpdateId;
        cartUpdateState.pendingRequest = updateId;

        // Ejecutar actualización con debounce
        cartUpdateState.updateTimeout = setTimeout(() => {
          updateCartItem(key, quantity, updateId, cartUpdateState, sectionId);
        }, 300);
      });

      // Event listener para blur (cuando pierde el foco) - manejar números de múltiples dígitos
      input.addEventListener('blur', function(e) {
        // Obtener el valor como string primero para preservar todos los dígitos
        let inputValue = e.target.value.trim();
        
        // Si está vacío, usar el último valor válido
        if (inputValue === '') {
          e.target.value = lastValue;
          // Disparar el evento del custom element para sincronizar
          const event = new CustomEvent('cart:quantity', {
            bubbles: true,
            detail: [itemKey, lastValue, selector]
          });
          selector.dispatchEvent(event);
          return;
        }

        // Remover cualquier carácter que no sea número
        inputValue = inputValue.replace(/[^0-9]/g, '');
        
        // Si después de limpiar está vacío, usar el último valor válido
        if (inputValue === '' || inputValue === '0') {
          e.target.value = lastValue;
          const event = new CustomEvent('cart:quantity', {
            bubbles: true,
            detail: [itemKey, lastValue, selector]
          });
          selector.dispatchEvent(event);
          return;
        }

        // Parsear el valor completo
        const currentValue = parseInt(inputValue, 10);
        
        // Validar que sea un número válido
        if (isNaN(currentValue) || currentValue < 1) {
          // Si es inválido, restaurar el último valor válido
          e.target.value = lastValue;
          const event = new CustomEvent('cart:quantity', {
            bubbles: true,
            detail: [itemKey, lastValue, selector]
          });
          selector.dispatchEvent(event);
          return;
        }

        // Actualizar el valor en el input para asegurar consistencia (en caso de que hubiera caracteres no numéricos)
        if (e.target.value !== currentValue.toString()) {
          e.target.value = currentValue;
        }

        // VALIDACIÓN: Verificar mínimo de 25ft para productos vendidos por FT
        let finalQuantity = currentValue;
        const validation = validateMinimumQuantity(cartItem, currentValue);
        if (!validation.valid) {
          // Si es menor a 25ft, establecer automáticamente a 25ft
          finalQuantity = 25;
          
          // Actualizar el input a 25
          e.target.value = 25;
          
          // Mostrar mensaje de error informativo
          showCartError(cartItem, validation.message);
        } else {
          // Remover mensajes de error si la validación pasó
          const existingError = cartItem.querySelector('.cart-item-error');
          if (existingError) {
            existingError.remove();
          }
        }

        // Solo actualizar si el valor cambió (o si fue corregido a 25)
        if (finalQuantity !== lastValue) {
          lastValue = finalQuantity;
          
          // Forzar actualización inmediata en blur
          if (cartUpdateState.updateTimeout) {
            clearTimeout(cartUpdateState.updateTimeout);
          }
          if (cartUpdateState.abortController) {
            cartUpdateState.abortController.abort();
          }
          cartUpdateState.abortController = new AbortController();
          const updateId = ++cartUpdateState.lastUpdateId;
          cartUpdateState.pendingRequest = updateId;
          updateCartItem(itemKey, finalQuantity, updateId, cartUpdateState, sectionId);
        }
      });

      // Event listener para Enter (cuando presiona Enter en el input)
      input.addEventListener('keydown', function(e) {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault(); // Prevenir submit del formulario si existe
          
          // Obtener el valor actual
          let inputValue = e.target.value.trim();
          
          // Si está vacío, usar el último valor válido
          if (inputValue === '') {
            e.target.value = lastValue;
            return;
          }

          // Remover cualquier carácter que no sea número
          inputValue = inputValue.replace(/[^0-9]/g, '');
          
          // Si después de limpiar está vacío, usar el último valor válido
          if (inputValue === '' || inputValue === '0') {
            e.target.value = lastValue;
            return;
          }

          // Parsear el valor completo
          const currentValue = parseInt(inputValue, 10);
          
          // Validar que sea un número válido
          if (isNaN(currentValue) || currentValue < 1) {
            // Si es inválido, restaurar el último valor válido
            e.target.value = lastValue;
            return;
          }

          // Actualizar el valor en el input para asegurar consistencia
          if (e.target.value !== currentValue.toString()) {
            e.target.value = currentValue;
          }

          // VALIDACIÓN: Verificar mínimo de 25ft para productos vendidos por FT
          let finalQuantity = currentValue;
          const validation = validateMinimumQuantity(cartItem, currentValue);
          if (!validation.valid) {
            // Si es menor a 25ft, establecer automáticamente a 25ft
            finalQuantity = 25;
            e.target.value = 25;
            showCartError(cartItem, validation.message);
          } else {
            // Remover mensajes de error si la validación pasó
            const existingError = cartItem.querySelector('.cart-item-error');
            if (existingError) {
              existingError.remove();
            }
          }

          // Actualizar lastValue
          lastValue = finalQuantity;

          // Forzar actualización inmediata al presionar Enter
          if (cartUpdateState.updateTimeout) {
            clearTimeout(cartUpdateState.updateTimeout);
          }
          if (cartUpdateState.abortController) {
            cartUpdateState.abortController.abort();
          }
          cartUpdateState.abortController = new AbortController();
          const updateId = ++cartUpdateState.lastUpdateId;
          cartUpdateState.pendingRequest = updateId;
          updateCartItem(itemKey, finalQuantity, updateId, cartUpdateState, sectionId);
          
          // Quitar el foco del input después de Enter
          e.target.blur();
        }
      });

      // No necesitamos escuchar 'input' aquí porque el custom element quantity-selector
      // ya maneja eso y dispara el evento 'cart:quantity' que estamos escuchando arriba
    });

    // Interceptar clicks en botones de eliminar
    document.addEventListener('click', function(e) {
      const removeButton = e.target.closest('[data-cartitem-remove]');
      if (removeButton) {
        e.preventDefault();
        const cartItem = removeButton.closest('[data-cartitem]');
        if (cartItem) {
          const itemKey = cartItem.getAttribute('data-cartitem-key');
          if (itemKey) {
            cartItem.classList.add('removing');
            if (cartUpdateState.updateTimeout) {
              clearTimeout(cartUpdateState.updateTimeout);
            }
            if (cartUpdateState.abortController) {
              cartUpdateState.abortController.abort();
            }
            cartUpdateState.abortController = new AbortController();
            const updateId = ++cartUpdateState.lastUpdateId;
            cartUpdateState.pendingRequest = updateId;
            updateCartItem(itemKey, 0, updateId, cartUpdateState, sectionId);
          }
        }
      }
    }, true);
  }

  function updateCartItem(key, quantity, updateId, state, sectionId) {
    // Validar que esta es la petición actual
    if (state.pendingRequest !== updateId) {
      return; // Esta petición ya no es la más reciente
    }

    // Validar inputs antes de hacer la petición
    if (!key || key === '' || typeof key !== 'string') {
      console.error('Invalid cart item key:', key);
      state.pendingRequest = null;
      return;
    }

    if (isNaN(quantity) || quantity < 0) {
      console.error('Invalid quantity:', quantity);
      state.pendingRequest = null;
      return;
    }

    const fetchBody = {
      id: key,
      quantity: quantity
    };

    // Realizar la petición con AbortController
    fetch(`${window.Theme.routes.cart_change_url}.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(fetchBody),
      signal: state.abortController.signal
    })
    .then(response => {
      // Validar que la respuesta sea exitosa
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      // Verificar que esta sigue siendo la petición actual
      if (state.pendingRequest !== updateId) {
        return; // Descartar, hay una petición más reciente
      }

      // Validar estructura de la respuesta
      if (!data || typeof data !== 'object') {
        throw new Error('Invalid response data');
      }

      // Manejar errores de validación de Shopify
      if (data.status === 422) {
        handleInvalidQuantity(data.message || 'Invalid quantity', updateId, state, sectionId);
      } else {
        // Validar que data.items existe antes de usarlo
        if (!data.items || !Array.isArray(data.items)) {
          console.error('Invalid cart response: missing items array');
          // Recargar el carrito para obtener datos válidos
          reloadCart(sectionId);
          return;
        }
        handleCartUpdate(data, updateId, state, sectionId);
      }
    })
    .catch(error => {
      // Ignorar errores de abort
      if (error.name === 'AbortError') {
        return;
      }

      // Verificar que esta sigue siendo la petición actual
      if (state.pendingRequest !== updateId) {
        return;
      }

      console.error('Error updating cart:', error);
      
      // En caso de error, recargar el carrito para sincronizar
      reloadCart(sectionId);
    })
    .finally(() => {
      if (state.pendingRequest === updateId) {
        state.pendingRequest = null;
        state.abortController = null;
      }
    });
  }

  function handleInvalidQuantity(errorMsg, updateId, state, sectionId) {
    // Si otra petición está en progreso, descartar esta
    if (state.pendingRequest !== updateId) {
      return;
    }

    // Mostrar mensaje de error (puedes personalizar esto)
    console.warn('Invalid quantity:', errorMsg);

    // Obtener el carrito actualizado
    fetch(`${window.Theme.routes.cart_url}.js`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json'
      }
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(cartData => {
      if (state.pendingRequest !== updateId) {
        return;
      }

      if (!cartData || !cartData.items || !Array.isArray(cartData.items)) {
        console.error('Invalid cart data received');
        reloadCart(sectionId);
        return;
      }

      handleCartUpdate(cartData, updateId, state, sectionId);
    })
    .catch(error => {
      console.error('Error fetching cart:', error);
      reloadCart(sectionId);
    });
  }

  function handleCartUpdate(response, updateId, state, sectionId) {
    // Verificar que esta sigue siendo la petición actual
    if (state.pendingRequest !== updateId) {
      return;
    }

    // VALIDACIÓN CRÍTICA: Verificar que response y response.items existan
    if (!response) {
      console.error('Response is undefined or null');
      reloadCart(sectionId);
      return;
    }

    if (!response.items) {
      console.error('Response.items is undefined');
      reloadCart(sectionId);
      return;
    }

    if (!Array.isArray(response.items)) {
      console.error('Response.items is not an array');
      reloadCart(sectionId);
      return;
    }

    // Si el carrito está vacío, recargar la página
    if (response.items.length === 0) {
      window.location.href = window.Theme.routes.cart_url;
      return;
    }

    // Disparar evento para actualizar el contador del carrito
    const countEvent = new CustomEvent('cartcount:update', {
      detail: response
    });
    window.dispatchEvent(countEvent);

    // Recargar la sección del carrito usando AsyncView
    if (window.AsyncView && window.AsyncView.load) {
      window.AsyncView.load(window.Theme.routes.cart_url, sectionId)
        .then(({ html }) => {
          // Verificar que esta sigue siendo la petición actual
          if (state.pendingRequest !== updateId) {
            return;
          }

          // Actualizar el DOM con el nuevo HTML
          updateCartDOM(html, sectionId);
        })
        .catch(error => {
          console.error('Error loading cart view:', error);
          window.location.reload();
        });
    } else {
      // Fallback: recargar la página si AsyncView no está disponible
      window.location.reload();
    }
  }

  function updateCartDOM(html, sectionId) {
    if (!html) {
      console.error('No HTML received');
      return;
    }

    // Actualizar la lista de items del carrito
    const cartItemList = document.querySelector('[data-cart-item-list]');
    if (cartItemList && html.list) {
      cartItemList.innerHTML = html.list;
    }

    // Actualizar totales
    const totals = document.querySelectorAll('[data-cart-total]');
    if (totals.length > 0 && html.cart_total) {
      totals.forEach(total => {
        total.innerHTML = html.cart_total;
      });
    }

    // Actualizar descuentos
    const discounts = document.querySelector('[data-cart-discounts]');
    if (discounts && html.discounts) {
      discounts.innerHTML = html.discounts;
    }

    // Actualizar barra de envío gratis
    const freeShippingBars = document.querySelectorAll('[data-free-shipping-bar]');
    if (freeShippingBars.length > 0 && html.free_shipping_bar) {
      freeShippingBars.forEach(bar => {
        bar.innerHTML = html.free_shipping_bar;
        bar.classList.add('free-shipping-bar--animate');
      });
    }

    // Re-inicializar los event listeners para los nuevos elementos
    // Esperar un poco más para que el custom element quantity-selector se inicialice
    setTimeout(() => {
      const cartSection = document.querySelector(`[data-section-type="static-cart"]`);
      if (cartSection) {
        const sectionDataScript = cartSection.querySelector('script[data-section-data]');
        if (sectionDataScript) {
          try {
            const sectionData = JSON.parse(sectionDataScript.textContent);
            patchStaticCart(sectionId, sectionData);
          } catch (e) {
            console.error('Error re-initializing cart:', e);
          }
        } else {
          // Si no hay script de datos, intentar de nuevo con los datos básicos
          patchStaticCart(sectionId, {});
        }
      }
    }, 300);
  }

  function reloadCart(sectionId) {
    // Recargar la página del carrito para sincronizar
    window.location.href = window.Theme.routes.cart_url;
  }

  // Función debounce simple
  function debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

})();

